import { utils, writeFile } from 'xlsx'

import type { useTranslation } from '@/components/i18n/hooks'

const NUMERIC_FORMATS = ['amount', 'number']

const formatMappings = {
  amount: '0.00',
  number: '0',
}

export function exportDataToExcel(
  data: object[],
  filename: string,
  options?: {
    headers?: string[]
    translate?: ReturnType<typeof useTranslation>
    extension?: 'csv' | 'xlsx'
    columnFormats?: { [key: string]: string }
    fromDate?: string
    toDate?: string
  }
) {
  if (!data || data.length === 0) {
    return null
  }

  // Ensure date objects are used in the data for date columns and 0 replace null for numbers
  const processedData = data.map(row => {
    const newRow: Record<string, string | Date | number> = { ...row }
    if (options?.columnFormats) {
      for (const key in options.columnFormats) {
        if (
          ['date', 'date_time'].includes(options.columnFormats[key].toLowerCase()) &&
          typeof newRow[key] === 'string'
        ) {
          newRow[key] = new Date(newRow[key])
        } else if (
          NUMERIC_FORMATS.includes(options.columnFormats[key].toLowerCase()) &&
          !newRow[key]
        ) {
          newRow[key] = 0
        } else if (NUMERIC_FORMATS.includes(options.columnFormats[key].toLowerCase())) {
          newRow[key] = Number(newRow[key])
        }
      }
    }
    return newRow
  })

  const workbook = utils.book_new()
  const worksheet = utils.json_to_sheet(processedData)
  const header = getColumnInfo(Object.keys(processedData[0]), worksheet, options)

  utils.sheet_add_aoa(worksheet, [header], { origin: 'A1' })

  if (options?.headers?.length) {
    utils.sheet_add_aoa(worksheet, [options.headers], { origin: 'A1' })
  }

  utils.book_append_sheet(workbook, worksheet, 'Sheet1')
  writeFile(
    workbook,
    `${filename}${options?.fromDate ? `-${options.fromDate}` : ''}${options?.toDate ? `-${options.toDate}` : ''}.${options?.extension || 'xlsx'}`,
    { compression: true }
  )
}

function getColumnInfo(
  headers: string[],
  worksheet?: any,
  options?: {
    translate?: ReturnType<typeof useTranslation>
    columnFormats?: { [key: string]: string }
  }
) {
  const { translate, columnFormats } = options || {}

  return headers.map((reportKey, index) => {
    const header = reportKey.split(/(?=[A-Z])/)
    let headerString = ''
    if (header.length > 0) {
      headerString = header[0].charAt(0).toUpperCase() + header[0].slice(1)
      header.forEach((column, idx) => {
        if (idx > 0) {
          headerString += ' ' + column
        }
      })
    }

    const columnHeader = translate ? translate(headerString) : headerString

    // Apply column formats
    let cellFormat = columnFormats ? columnFormats[reportKey] : null
    // Dates are handled in line 25
    if (cellFormat && cellFormat !== 'date') {
      const range = utils.decode_range(worksheet['!ref'])
      const col = utils.encode_col(index)
      for (let R = 1; R <= range.e.r; ++R) {
        const cellAddress = `${col}${R + 1}`
        if (!worksheet[cellAddress]) continue
        if (!Object.keys(formatMappings).includes(cellFormat)) {
          throw Error('Unsupported custom data format!')
        }
        worksheet[cellAddress].z = formatMappings[cellFormat as keyof typeof formatMappings]
      }
    }

    return columnHeader
  })
}
